/**
 * acesso-store — o que ESTA pessoa pode abrir, vindo do banco.
 *
 * Rodada PERMISSOES-ACESSO (migration 419 + `server/lib/acesso.ts`).
 *
 * Princípio: quem acessa o quê se decide na tela de Permissões (perfil +
 * departamento + exceção por pessoa), nunca por lista fixa de rotas no código.
 * Este arquivo é só o consumidor da API — ele NÃO decide nada.
 *
 * Esconder item de menu com estes hooks é CONFORTO. Quem barra de verdade é o
 * `requireTela` na rota da API: se alguém digitar a URL na mão, ou chamar o
 * endpoint direto, a resposta é a mesma.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';

import { api, hasToken } from './api';

/** De onde veio a decisão (o motor devolve isso pra tela de Permissões). */
export type OrigemAcesso = 'admin' | 'usuario' | 'departamento' | 'perfil' | 'nenhuma';

export type NaturezaAcesso = 'ver' | 'agir';

export interface AcessoTelaItem {
  chave: string;
  nome: string;
  grupo: string;
  natureza: NaturezaAcesso;
  permitido: boolean;
  origem: OrigemAcesso;
}

export type SujeitoTipo = 'perfil' | 'departamento' | 'usuario';

export interface AcessoRegra {
  id: string;
  chave: string;
  sujeito_tipo: SujeitoTipo;
  sujeito_id: string;
  permitido: 0 | 1;
  criado_em_iso: string;
}

export interface AcessosDoUsuario {
  usuario: {
    id: string;
    nome?: string;
    email?: string;
    isAdmin: boolean;
    perfil: string | null;
    departamentos: string[];
  };
  acessos: AcessoTelaItem[];
}

const KEYS = {
  meu: ['v1', 'acesso', 'meu'] as const,
  regras: ['v1', 'acesso', 'regras'] as const,
  doUsuario: (id: string) => ['v1', 'acesso', 'usuario', id] as const,
};

// ─── O MEU acesso (menu + porteiro do AdminLayout) ──────────────────────

/**
 * Tudo o que EU consigo abrir, com a origem de cada permissão.
 *
 * `carregando` existe pra ninguém piscar "Acesso restrito" na cara de quem
 * TEM acesso enquanto a lista chega — piscar bloqueio é regressão percebida.
 */
export function useMeusAcessos(): { itens: AcessoTelaItem[]; carregando: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: KEYS.meu,
    queryFn: () => api.get<{ items: AcessoTelaItem[] }>('/api/v1/acesso/meu'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return { itens: data?.items ?? [], carregando: isLoading };
}

/**
 * Versão com estado de carregamento — use quando a tela precisa DECIDIR
 * (o porteiro do AdminLayout). Para esconder item de menu, `usePodeAcessar`
 * basta.
 *
 * Admin responde `true` na hora, sem esperar a rede: o motor do servidor faz o
 * mesmo (`is_admin` passa por tudo), e assim o admin nunca vê o menu piscar.
 */
export function useAcessoDaTela(chave: string | null | undefined): {
  permitido: boolean;
  carregando: boolean;
} {
  const { user } = useAuth();
  const { itens, carregando } = useMeusAcessos();
  return useMemo(() => {
    // Sem chave não há tela governada — quem chama decide o que fazer com isso
    // (o AdminLayout mantém a rota admin-only).
    if (!chave) return { permitido: false, carregando: false };
    if (user?.isAdmin) return { permitido: true, carregando: false };
    if (carregando) return { permitido: false, carregando: true };
    const item = itens.find((i) => i.chave === chave);
    return { permitido: item?.permitido === true, carregando: false };
  }, [user?.isAdmin, chave, itens, carregando]);
}

/** Atalho booleano pra esconder menu/botão. Enquanto carrega devolve `false`. */
export function usePodeAcessar(chave: string | null | undefined): boolean {
  return useAcessoDaTela(chave).permitido;
}

// ─── Governo das regras (tela de Permissões — admin) ────────────────────

/** Resultado final de OUTRA pessoa: o que ela abre e de onde vem cada acesso. */
export function useAcessosDoUsuario(userId: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.doUsuario(userId ?? ''),
    queryFn: () => api.get<AcessosDoUsuario>(`/api/v1/acesso/usuario/${encodeURIComponent(userId!)}`),
    enabled: hasToken() && !!userId,
    staleTime: 30_000,
  });
}

/**
 * Regras vigentes (quem tem cada chave). A tela de Permissões usa pra saber
 * QUAL regra remover — a lista de acessos resolvidos traz só a origem, não o id.
 */
export function useAcessoRegras(): { regras: AcessoRegra[]; carregando: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: KEYS.regras,
    queryFn: () => api.get<{ items: AcessoRegra[] }>('/api/v1/acesso/regras'),
    enabled: hasToken(),
    staleTime: 30_000,
  });
  return { regras: data?.items ?? [], carregando: isLoading };
}

/**
 * Conceder / remover. Invalida tudo o que depende de acesso (inclusive o MEU
 * `/meu`: o admin pode estar ajustando a si mesmo).
 */
export function useAcessoActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidarTudo = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['v1', 'acesso'] });
  }, [qc]);

  const conceder = useCallback(
    async (params: {
      chave: string;
      sujeitoTipo: SujeitoTipo;
      sujeitoId: string;
      permitido?: boolean;
    }) => {
      const marca = `${params.chave}:${params.sujeitoTipo}:${params.sujeitoId}`;
      setPending(marca);
      try {
        await api.post('/api/v1/acesso/regras', {
          chave: params.chave,
          sujeitoTipo: params.sujeitoTipo,
          sujeitoId: params.sujeitoId,
          permitido: params.permitido ?? true,
        });
        await invalidarTudo();
      } finally {
        setPending(null);
      }
    },
    [invalidarTudo],
  );

  const remover = useCallback(
    async (regraId: string) => {
      setPending(regraId);
      try {
        await api.delete(`/api/v1/acesso/regras/${encodeURIComponent(regraId)}`);
        await invalidarTudo();
      } finally {
        setPending(null);
      }
    },
    [invalidarTudo],
  );

  return { conceder, remover, pending };
}

// ─── Copy pt-BR compartilhada ───────────────────────────────────────────

/**
 * slug técnico → rótulo legível ("comercial" → "Comercial"). Só capitaliza e
 * troca hífen/underline por espaço; quando existe nome cadastrado (perfil ou
 * departamento), prefira o nome real.
 */
export function nomeBonito(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
